import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiAward,
  FiBookOpen,
  FiCheckCircle,
  FiClock,
  FiMonitor,
  FiShield,
  FiTarget,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";
import Layout from "../../components/Layout";
import TrainingHero from "./TrainingHero";
import ContactSection4 from "../contact/ContactSection4";

const SITE_URL = "https://mbasolutions.co";
const BANNER = `${process.env.PUBLIC_URL}/assets/training-banner.png`;

const whyChoosePoints = [
  "Delivered by experienced trainers with practical industry knowledge",
  "Flexible classroom, online, and blended options for local and international teams",
  "Real-world case studies that connect learning to workplace decisions",
  "Clear, structured modules with assessments and measurable outcomes",
  "Trusted by corporates, SMEs, and operational leaders seeking stronger safety culture",
];

const keyFeatures = [
  {
    title: "Flexible Delivery",
    desc: "IOSH Managing Safely classroom training and online options available for individuals and corporate groups.",
    icon: FiMonitor,
  },
  {
    title: "3-Day Structured Program",
    desc: "A standard interactive program with practical modules and assessments designed for managers and supervisors.",
    icon: FiClock,
  },
  {
    title: "Expert Facilitation",
    desc: "Guided by trainers who understand operational realities across manufacturing, services, and project environments.",
    icon: FiUsers,
  },
  {
    title: "Recognized Certificate",
    desc: "Earn an internationally recognized IOSH Managing Safely certificate upon successful completion.",
    icon: FiAward,
  },
  {
    title: "Practical Risk Tools",
    desc: "Apply workplace scenarios and risk-management tools you can use immediately with your team.",
    icon: FiTarget,
  },
];

const whoShouldTake = [
  "Team leaders and supervisors",
  "Department managers in corporate, industrial, and service sectors",
  "Business owners responsible for workplace safety",
  "Project leads handling compliance and operational risk",
];

const learningOutcomes = [
  "Assessing and controlling workplace risks",
  "Identifying hazards across different environments",
  "Investigating incidents and learning from them",
  "Understanding legal responsibilities and compliance expectations",
  "Measuring safety performance effectively",
  "Protecting people and supporting responsible environmental practices",
];

const benefits = [
  {
    title: "Recognized Qualification",
    desc: "Earn the IOSH Managing Safely certificate, valued by employers worldwide.",
    icon: FiAward,
  },
  {
    title: "Leadership Confidence",
    desc: "Managers and supervisors gain the ability to make informed safety decisions.",
    icon: FiTrendingUp,
  },
  {
    title: "Improved Compliance",
    desc: "Help organizations meet regulatory expectations and reduce avoidable risk.",
    icon: FiShield,
  },
  {
    title: "Practical Skills",
    desc: "Case studies and exercises ensure hands-on learning that transfers to the job.",
    icon: FiBookOpen,
  },
  {
    title: "Organizational Growth",
    desc: "A safer workplace supports fewer incidents, stronger productivity, and higher trust.",
    icon: FiUsers,
  },
];

const deliveryFormats = [
  {
    title: "Classroom Training",
    desc: "Interactive face-to-face sessions led by experienced instructors.",
  },
  {
    title: "Online Training",
    desc: "Live virtual sessions with trainer support for remote and multi-site teams.",
  },
  {
    title: "Blended Learning",
    desc: "A mix of digital modules and facilitated sessions for maximum flexibility.",
  },
];

const relatedCourses = [
  { name: "Highfield (HABC) UK Course", link: "/highfield-training" },
  { name: "ISO Lead Auditor Course", link: "/iso-lead-auditor-training" },
  { name: "Corporate Training", link: "/corporate-training" },
  { name: "HACCP Level 2 / 3 / 4", link: "/haccp-training" },
  { name: "PECB Training", link: "/pecb-training" },
  { name: "ISO Awareness Courses", link: "/iso-lead-auditor-training" },
  { name: "All Training Programs", link: "/training" },
];

const SectionHeading = ({ title, subtitle }) => (
  <div className="text-center max-w-3xl mx-auto mb-12 md:mb-14">
    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
      {title}
    </h2>
    <div
      className="w-20 h-1 bg-[#F86624] mx-auto rounded-full mb-5"
      aria-hidden="true"
    />
    {subtitle && (
      <p className="text-gray-600 text-base md:text-lg leading-relaxed">
        {subtitle}
      </p>
    )}
  </div>
);

const IoshManagingSafelyTraining = () => {
  return (
    <Layout
      title="IOSH Managing Safely Course for Managers | MBA Solutions"
      description="IOSH Managing Safely course from MBA Solutions — practical health and safety training for managers and supervisors covering risk assessment, hazard control, compliance, and safer workplace leadership."
      keywords="IOSH Managing Safely, IOSH Managing Safely course, IOSH training for managers, workplace health and safety training, risk management training, MBA Solutions IOSH"
      canonical={`${SITE_URL}/iosh-managing-safely-training`}
    >
      <TrainingHero
        badge="Health & Safety Leadership"
        title="IOSH Managing Safely Course"
        subtitle="A globally recognized health and safety training program designed for managers and supervisors — building the skills to manage workplace risks and lead safer, more compliant teams."
        icon={FiShield}
        primaryCta={{ label: "Contact Us", to: "/contact" }}
        secondaryCta={{ label: "All Training", to: "/training" }}
      />

      {/* What is IOSH */}
      <section className="bg-white py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              What is the IOSH Managing Safely Course?
            </h2>
            <div
              className="w-16 h-1 bg-[#F86624] rounded-full mb-6"
              aria-hidden="true"
            />
            <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-4">
              The IOSH Managing Safely course is a practical program created by
              the Institution of Occupational Safety and Health (IOSH). It is
              tailored for managers, supervisors, and team leaders across
              industries who are responsible for employee health, safety, and
              compliance.
            </p>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              Unlike basic safety awareness training, this program focuses on
              management responsibilities — giving leaders the confidence to
              recognize hazards, evaluate risks, and implement controls that
              protect people and business performance.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="relative"
          >
            <img
              src={BANNER}
              alt="Managers completing IOSH Managing Safely health and safety training"
              className="rounded-3xl shadow-2xl object-cover w-full h-[300px] md:h-[400px]"
              loading="lazy"
            />
            <div
              className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-[var(--bg-primary)]/25 to-[#F86624]/10"
              aria-hidden="true"
            />
          </motion.div>
        </div>
      </section>

      {/* Why choose MBA */}
      <section className="bg-gray-50 py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            title="Why Choose MBA Solutions as Your IOSH Training Provider?"
            subtitle="MBA Solutions delivers impactful health and safety programs trusted by corporates, SMEs, and operational teams seeking practical, management-focused learning."
          />
          <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
            <ul className="space-y-4 list-none">
              {whyChoosePoints.map((point) => (
                <li
                  key={point}
                  className="flex items-start gap-3 text-gray-700"
                >
                  <FiCheckCircle
                    size={18}
                    className="text-[var(--bg-primary)] mt-0.5 flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-sm md:text-base leading-relaxed">
                    {point}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-12">
            <h3 className="text-center text-lg font-bold text-gray-900 mb-5">
              Related Courses
            </h3>
            <div className="flex flex-wrap justify-center gap-3">
              {relatedCourses.map((course) => (
                <Link
                  key={course.name}
                  to={course.link}
                  className="inline-flex items-center px-4 py-2.5 rounded-full border border-gray-200 bg-white text-sm font-medium text-gray-800 hover:border-[var(--bg-primary)]/40 hover:text-[var(--bg-primary)] transition-colors"
                >
                  {course.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key features */}
      <section className="bg-white py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            title="Key Features of IOSH Managing Safely Training"
            subtitle="Structured for maximum value with an engaging learning experience for busy managers and supervisors."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
            {keyFeatures.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.article
                  key={item.title}
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="bg-gray-50 border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-lg hover:bg-white transition-all"
                >
                  <div className="w-11 h-11 rounded-xl bg-[var(--bg-primary)]/10 text-[var(--bg-primary)] flex items-center justify-center mb-4">
                    <Icon size={20} aria-hidden="true" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {item.desc}
                  </p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who should take + What you learn */}
      <section className="bg-gray-50 py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl border border-gray-100 p-7 md:p-8 shadow-sm"
          >
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
              Who Should Take the IOSH Managing Safely Course?
            </h2>
            <div className="w-16 h-1 bg-[#F86624] rounded-full mb-5" aria-hidden="true" />
            <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-5">
              Designed for managers and supervisors in any sector who need to
              manage health and safety within their teams. Ideal for:
            </p>
            <ul className="space-y-3 list-none">
              {whoShouldTake.map((item) => (
                <li key={item} className="flex items-start gap-3 text-gray-700">
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-[#F86624] mt-2 flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-sm md:text-base">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-600 mt-5 leading-relaxed">
              Suitable for both local and international organizations seeking
              consistent management capability in workplace safety.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.08 }}
            className="bg-white rounded-2xl border border-gray-100 p-7 md:p-8 shadow-sm"
          >
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
              What You Will Learn
            </h2>
            <div className="w-16 h-1 bg-[#F86624] rounded-full mb-5" aria-hidden="true" />
            <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-5">
              Participants build a strong understanding of safety
              responsibilities through modules covering:
            </p>
            <ul className="space-y-3 list-none">
              {learningOutcomes.map((item) => (
                <li key={item} className="flex items-start gap-3 text-gray-700">
                  <FiCheckCircle
                    size={17}
                    className="text-[var(--bg-primary)] mt-0.5 flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-sm md:text-base leading-relaxed">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-white py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            title="Benefits of IOSH Managing Safely Course"
            subtitle="Completing this program brings measurable value for individuals and organizations."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.article
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="rounded-2xl border border-gray-100 bg-gray-50 p-6 hover:bg-white hover:shadow-lg transition-all"
                >
                  <div className="w-11 h-11 rounded-xl bg-[var(--bg-primary)]/10 text-[var(--bg-primary)] flex items-center justify-center mb-4">
                    <Icon size={20} aria-hidden="true" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {item.desc}
                  </p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Duration & delivery */}
      <section className="bg-gray-50 py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            title="Course Duration and Delivery"
            subtitle="The IOSH Managing Safely 3-day training is available in multiple formats so you can choose what fits your team."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {deliveryFormats.map((item, index) => (
              <motion.article
                key={item.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
                className="bg-white rounded-2xl border border-gray-100 p-6 md:p-7 shadow-sm text-center"
              >
                <h3 className="text-lg font-bold text-[var(--bg-primary)] mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.desc}
                </p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Career impact */}
      <section className="bg-white py-14 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Career and Organizational Impact
          </h2>
          <div
            className="w-16 h-1 bg-[#F86624] mx-auto rounded-full mb-6"
            aria-hidden="true"
          />
          <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-4">
            For managers and supervisors, completing the IOSH Managing Safely
            course strengthens professional profiles and demonstrates the
            ability to manage safety effectively.
          </p>
          <p className="text-gray-600 text-base md:text-lg leading-relaxed">
            For organizations, it supports a stronger safety culture, fewer
            incidents, and clearer compliance — helping build workplaces where
            people feel protected and performance can grow with confidence.
          </p>
        </div>
      </section>

      {/* Contact — Certifications enquiry form */}
      <section
        className="bg-gray-50 pt-10 md:pt-14"
        aria-label="Contact for training courses"
      >
        <div className="max-w-3xl mx-auto px-4 text-center mb-2">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Contact for Training Courses
          </h2>
          <div
            className="w-20 h-1 bg-[#F86624] mx-auto rounded-full mb-4"
            aria-hidden="true"
          />
          <p className="text-gray-600">
            Ask about IOSH Managing Safely schedules, fees, and group bookings —
            our training team will respond shortly.
          </p>
        </div>
        <ContactSection4 />
      </section>

      {/* Closing CTA */}
      <section className="relative overflow-hidden py-14 md:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1a1a2e] via-[#3d1f5c] to-[var(--bg-primary)] text-white">
        <div className="relative max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Enroll in IOSH Managing Safely Training Today
          </h2>
          <div className="w-20 h-1 bg-[#F86624] mx-auto rounded-full mb-5" aria-hidden="true" />
          <p className="text-white/85 mb-8 leading-relaxed">
            The IOSH Managing Safely Course is more than a certificate — it is a
            practical solution for businesses that want leaders who can manage
            safety with confidence. Equip supervisors, managers, and business
            owners with tools to create a safer, more successful organization.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#F86624] hover:bg-[#e55a1a] text-white font-semibold shadow-lg transition-all"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default IoshManagingSafelyTraining;
